<script setup>
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import DatePicker from 'vue-datepicker-next';
import { format, addMinutes } from 'date-fns';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import AudioRecorder from 'dashboard/components/widgets/WootWriter/AudioRecorder.vue';
import MediaUploadAPI from 'dashboard/api/agents/media';
import { AUDIO_FORMATS } from 'shared/constants/messages';
import { DATE_PICKER_LANG_PT_BR } from 'dashboard/helper/datePickerLang';
import { inOneHour, minSchedulable, isPastWindow } from './scheduleShortcuts';

const props = defineProps({
  conversationId: { type: Number, required: true },
  initialContent: { type: String, default: '' },
  windowClosesAt: { type: Date, default: null },
  audioRecordFormat: { type: String, default: AUDIO_FORMATS.MP3 },
});
const emit = defineEmits(['close', 'scheduled']);

const MAX_STEPS = 10;
const DEFAULT_DELAY_MINUTES = 5;
const ACCEPTED_FILES = 'image/*,audio/*,video/mp4,.pdf';

const store = useStore();
const { t } = useI18n();

let nextStepId = 0;
const buildStep = (content = '') => {
  nextStepId += 1;
  return {
    id: nextStepId,
    content,
    delayMinutes: DEFAULT_DELAY_MINUTES,
    attachment: null,
    uploading: false,
    uploadError: false,
  };
};

const firstStep = buildStep(props.initialContent);
firstStep.delayMinutes = 0;

const startsAt = ref(inOneHour());
const stopOnReply = ref(true);
const steps = ref([firstStep]);
const recordingStepId = ref(null);
const isSubmitting = ref(false);
const fileInputs = ref({});
const recorderRef = ref(null);

const setFileInput = (id, el) => {
  fileInputs.value[id] = el;
};

const scheduleTimes = computed(() => {
  // mesmo clamp do envio: a prévia não pode mostrar um horário que já passou
  const minimum = minSchedulable();
  let current =
    !startsAt.value || startsAt.value < minimum ? minimum : startsAt.value;
  return steps.value.map((step, index) => {
    if (index > 0) {
      current = addMinutes(current, Number(step.delayMinutes) || 0);
    }
    return current;
  });
});

const lastTime = computed(
  () => scheduleTimes.value[scheduleTimes.value.length - 1]
);
const tooSoon = computed(
  () => !startsAt.value || startsAt.value < minSchedulable()
);
const pastWindow = computed(() =>
  isPastWindow(lastTime.value, props.windowClosesAt)
);
const hasEmptyStep = computed(() =>
  steps.value.some(step => !step.content.trim() && !step.attachment)
);
const isUploading = computed(() => steps.value.some(step => step.uploading));
const canConfirm = computed(
  () =>
    !tooSoon.value &&
    !hasEmptyStep.value &&
    !isUploading.value &&
    !isSubmitting.value &&
    steps.value.length > 0 &&
    steps.value.length <= MAX_STEPS
);
const canAddStep = computed(() => steps.value.length < MAX_STEPS);

const formatTime = date => (date ? format(date, 'HH:mm') : '');

const addStep = () => {
  if (!canAddStep.value) return;
  steps.value.push(buildStep());
};

const removeStep = index => {
  if (steps.value.length === 1) return;
  const [removed] = steps.value.splice(index, 1);
  if (recordingStepId.value === removed.id) recordingStepId.value = null;
  if (steps.value[0]) steps.value[0].delayMinutes = 0;
};

const moveStep = (index, direction) => {
  const target = index + direction;
  if (target < 0 || target >= steps.value.length) return;
  const list = steps.value;
  [list[index], list[target]] = [list[target], list[index]];
  list[0].delayMinutes = 0;
};

const attach = async (step, file) => {
  if (!file) return;
  step.uploading = true;
  step.uploadError = false;
  try {
    const { data } = await MediaUploadAPI.upload(file);
    step.attachment = {
      signed_id: data.signed_id,
      name: file.name,
      content_type: file.type,
      url: data.file_url,
    };
  } catch {
    step.uploadError = true;
  } finally {
    step.uploading = false;
  }
};

const onFilePicked = (step, event) => {
  const file = event.target?.files?.[0];
  attach(step, file);
  if (event.target) event.target.value = '';
};

const openFilePicker = step => {
  fileInputs.value[step.id]?.click();
};

const removeAttachment = step => {
  step.attachment = null;
  step.uploadError = false;
};

const toggleRecorder = step => {
  recordingStepId.value = recordingStepId.value === step.id ? null : step.id;
};

const stopRecording = () => {
  recorderRef.value?.stopRecording?.();
};

const onFinishRecord = (step, payload) => {
  const file = payload?.file || payload;
  recordingStepId.value = null;
  attach(step, file);
};

const buildPayload = () => ({
  // O agente pode levar minutos montando os passos depois de clicar em
  // "Agora": sem o clamp, o relógio andou e o starts_at guardado fica abaixo
  // do mínimo aceito pelo backend.
  starts_at: (startsAt.value < minSchedulable()
    ? minSchedulable()
    : startsAt.value
  ).toISOString(),
  stop_on_reply: stopOnReply.value,
  steps: steps.value.map((step, index) => ({
    content: step.content,
    delay_minutes: index === 0 ? 0 : Number(step.delayMinutes) || 0,
    attachment_signed_ids: step.attachment ? [step.attachment.signed_id] : [],
  })),
});

const confirm = async () => {
  if (!canConfirm.value) return;
  isSubmitting.value = true;
  try {
    const data = await store.dispatch('scheduledMessages/createSequence', {
      conversationId: props.conversationId,
      payload: buildPayload(),
    });
    emit('scheduled', data);
  } catch (error) {
    const errors = error?.response?.data?.errors;
    useAlert(
      errors?.join(', ') ||
        t('CONVERSATION.SCHEDULED_MESSAGES.SEQUENCE_CREATE_ERROR')
    );
  } finally {
    isSubmitting.value = false;
  }
};

const disabledDate = date => date < new Date(new Date().setHours(0, 0, 0, 0));
const disabledTime = date => date < minSchedulable();
</script>

<template>
  <div
    class="flex fixed inset-0 z-50 justify-center items-center p-4 bg-n-alpha-3"
    @click.self="emit('close')"
  >
    <div
      class="flex flex-col gap-4 p-5 w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl border shadow-xl bg-n-solid-1 border-n-weak"
    >
      <div class="flex justify-between items-center">
        <span class="text-base font-medium text-n-slate-12">
          {{ t('CONVERSATION.SCHEDULED_MESSAGES.SEQUENCE_TITLE') }}
        </span>
        <NextButton
          icon="i-lucide-x"
          ghost
          slate
          xs
          :label="t('CONVERSATION.SCHEDULED_MESSAGES.CLOSE')"
          @click="emit('close')"
        />
      </div>

      <div class="flex flex-col gap-2">
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('CONVERSATION.SCHEDULED_MESSAGES.SEQUENCE_START') }}
        </span>
        <div class="flex flex-wrap gap-2 items-center">
          <DatePicker
            v-model:value="startsAt"
            type="datetime"
            :minute-step="5"
            :lang="DATE_PICKER_LANG_PT_BR"
            :disabled-date="disabledDate"
            :disabled-time="disabledTime"
            data-testid="schedule-sequence-start"
          />
          <NextButton
            faded
            slate
            xs
            data-testid="schedule-sequence-now"
            :label="t('CONVERSATION.SCHEDULED_MESSAGES.SEQUENCE_NOW')"
            @click="startsAt = minSchedulable()"
          />
        </div>
        <p v-if="tooSoon" class="text-xs text-n-ruby-11">
          {{ t('CONVERSATION.SCHEDULED_MESSAGES.TOO_SOON') }}
        </p>
        <p v-else-if="pastWindow" class="text-xs text-n-amber-11">
          {{ t('CONVERSATION.SCHEDULED_MESSAGES.WINDOW_WARNING') }}
        </p>
      </div>

      <div class="flex gap-2 items-center text-sm text-n-slate-12">
        <Switch v-model="stopOnReply" />
        <span>
          {{ t('CONVERSATION.SCHEDULED_MESSAGES.SEQUENCE_STOP_ON_REPLY') }}
        </span>
      </div>

      <div class="flex flex-col gap-3">
        <div
          v-for="(step, index) in steps"
          :key="step.id"
          class="flex flex-col gap-2 p-3 rounded-xl border border-n-weak bg-n-solid-2"
        >
          <div class="flex justify-between items-center">
            <span class="text-sm font-medium text-n-slate-12">
              {{
                t('CONVERSATION.SCHEDULED_MESSAGES.SEQUENCE_STEP', {
                  n: index + 1,
                })
              }}
            </span>
            <div class="flex gap-1 items-center">
              <NextButton
                icon="i-lucide-arrow-up"
                ghost
                slate
                xs
                :disabled="index === 0"
                @click="moveStep(index, -1)"
              />
              <NextButton
                icon="i-lucide-arrow-down"
                ghost
                slate
                xs
                :disabled="index === steps.length - 1"
                @click="moveStep(index, 1)"
              />
              <NextButton
                icon="i-lucide-trash-2"
                ghost
                ruby
                xs
                :disabled="steps.length === 1"
                :label="
                  t('CONVERSATION.SCHEDULED_MESSAGES.SEQUENCE_REMOVE_STEP')
                "
                @click="removeStep(index)"
              />
            </div>
          </div>

          <textarea
            v-model="step.content"
            rows="3"
            :data-testid="`schedule-sequence-step-content-${index}`"
            class="p-2 w-full text-sm rounded-lg border resize-y border-n-weak bg-n-solid-1 text-n-slate-12"
          />

          <div class="flex flex-wrap gap-2 items-center">
            <input
              :ref="el => setFileInput(step.id, el)"
              type="file"
              class="hidden"
              :accept="ACCEPTED_FILES"
              :data-testid="`schedule-sequence-step-file-${index}`"
              @change="onFilePicked(step, $event)"
            />
            <NextButton
              icon="i-lucide-paperclip"
              faded
              slate
              xs
              :disabled="step.uploading"
              :label="t('CONVERSATION.SCHEDULED_MESSAGES.SEQUENCE_ATTACH')"
              @click="openFilePicker(step)"
            />
            <NextButton
              icon="i-lucide-mic"
              faded
              slate
              xs
              :disabled="step.uploading"
              :label="t('CONVERSATION.SCHEDULED_MESSAGES.SEQUENCE_RECORD')"
              @click="toggleRecorder(step)"
            />
            <span v-if="step.uploading" class="text-xs text-n-slate-11">
              {{ t('CONVERSATION.SCHEDULED_MESSAGES.SEQUENCE_UPLOADING') }}
            </span>
            <span v-else-if="step.uploadError" class="text-xs text-n-ruby-11">
              {{ t('CONVERSATION.SCHEDULED_MESSAGES.SEQUENCE_UPLOAD_ERROR') }}
            </span>
            <span
              v-else-if="step.attachment"
              class="flex gap-1 items-center text-xs text-n-slate-11"
            >
              {{ step.attachment.name }}
              <NextButton
                icon="i-lucide-x"
                ghost
                slate
                xs
                :label="
                  t(
                    'CONVERSATION.SCHEDULED_MESSAGES.SEQUENCE_REMOVE_ATTACHMENT'
                  )
                "
                @click="removeAttachment(step)"
              />
            </span>
          </div>

          <div
            v-if="recordingStepId === step.id"
            class="flex flex-col gap-2 p-2 rounded-lg bg-n-solid-1"
          >
            <AudioRecorder
              :ref="el => (recorderRef = el)"
              :audio-record-format="audioRecordFormat"
              @finish-record="onFinishRecord(step, $event)"
            />
            <NextButton
              faded
              slate
              xs
              :label="t('CONVERSATION.SCHEDULED_MESSAGES.SEQUENCE_STOP_RECORD')"
              @click="stopRecording"
            />
          </div>

          <div class="flex flex-wrap gap-3 items-center">
            <label
              v-if="index > 0"
              class="flex gap-2 items-center text-xs text-n-slate-11"
            >
              {{ t('CONVERSATION.SCHEDULED_MESSAGES.SEQUENCE_DELAY') }}
              <input
                v-model.number="step.delayMinutes"
                type="number"
                min="1"
                :data-testid="`schedule-sequence-step-delay-${index}`"
                class="px-2 py-1 w-20 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12"
              />
              {{ t('CONVERSATION.SCHEDULED_MESSAGES.SEQUENCE_MINUTES') }}
            </label>
            <span class="text-xs text-n-teal-11">
              {{
                t('CONVERSATION.SCHEDULED_MESSAGES.SEQUENCE_SENDS_AT', {
                  time: formatTime(scheduleTimes[index]),
                })
              }}
            </span>
          </div>
        </div>
      </div>

      <div class="flex gap-3 items-center">
        <NextButton
          icon="i-lucide-plus"
          faded
          slate
          sm
          :disabled="!canAddStep"
          data-testid="schedule-sequence-add-step"
          :label="t('CONVERSATION.SCHEDULED_MESSAGES.SEQUENCE_ADD_STEP')"
          @click="addStep"
        />
        <span class="text-xs text-n-slate-11">
          {{
            t('CONVERSATION.SCHEDULED_MESSAGES.SEQUENCE_MAX', {
              max: MAX_STEPS,
            })
          }}
        </span>
      </div>

      <p v-if="hasEmptyStep" class="text-xs text-n-ruby-11">
        {{ t('CONVERSATION.SCHEDULED_MESSAGES.SEQUENCE_STEP_EMPTY') }}
      </p>

      <div class="flex gap-2 justify-end">
        <NextButton
          faded
          slate
          sm
          :label="t('CONVERSATION.SCHEDULED_MESSAGES.CANCEL')"
          @click="emit('close')"
        />
        <NextButton
          blue
          sm
          data-testid="schedule-sequence-confirm"
          :label="t('CONVERSATION.SCHEDULED_MESSAGES.SEQUENCE_CONFIRM')"
          :disabled="!canConfirm"
          :is-loading="isSubmitting"
          @click="confirm"
        />
      </div>
    </div>
  </div>
</template>
